import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, ButtonProps, Menu, MenuItem, MenuItemProps, Popover, Separator, Text } from '@/components/core';
import { AddIcon, CheckmarkIcon } from '@/components/icons';
import { useAgentIcon, useGetAgentFromPath } from '@/hooks';
import { toAgentId, useGetAgent, useListAgents } from '@/hooks/api';
import { OmitChildren } from '@/utils';

type AgentMenuItemProps = OmitChildren<MenuItemProps> & {
  agentId: string;
  isSelected?: boolean;
};

const IAgentMenuItem = ({ agentId, isSelected, ...rest }: AgentMenuItemProps) => {
  const { data: agent } = useGetAgent(toAgentId(agentId));
  const agentIcon = useAgentIcon({ agentId, className: 'size-4' });

  return (
    <MenuItem className="hover:bg-gray-50" {...rest}>
      {agentIcon}
      <Text slot="label">{agent?.name}</Text>
      {isSelected ? (
        <>
          <div className="grow" />
          <CheckmarkIcon variant="success" />
        </>
      ) : null}
    </MenuItem>
  );
};

const AgentMenuItem = memo(IAgentMenuItem) as typeof IAgentMenuItem;

export type AgentSelectorMenuProps = ButtonProps;

const IAgentSelectorMenu = ({ children, ...rest }: AgentSelectorMenuProps) => {
  const { data: agents } = useListAgents();
  const navigate = useNavigate();

  const { data: currentAgent } = useGetAgentFromPath();

  const onAgentSelected = useCallback(
    async (agentId: string) => {
      return navigate({ to: '.', params: { agentId } });
    },
    [navigate]
  );

  if (!agents) return null;

  return (
    <MenuTrigger>
      <Button variant="plain" {...rest}>
        {children}
      </Button>
      <Popover>
        <Menu selectionMode="single" selectedKeys={currentAgent ? [currentAgent.agentId] : undefined}>
          {agents.data.map((p) => (
            <AgentMenuItem
              id={p.agentId}
              key={p.agentId}
              agentId={p.agentId}
              isSelected={currentAgent?.agentId === p.agentId}
              onAction={async () => onAgentSelected(p.agentId)}
            />
          ))}
          <Separator orientation="horizontal" />
          <MenuItem variant="plain" onAction={async () => navigate({ to: '/agents/new' })}>
            <AddIcon />
            <Text slot="label">New agent</Text>
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};

export const AgentSelectorMenu = memo(IAgentSelectorMenu) as typeof IAgentSelectorMenu;
